import * as React from "react";
import { Field, Input, Text, makeStyles, tokens } from "@fluentui/react-components";
import RichTextInput from "./RichTextInput";
import { RichTextValue, MentionUser } from "./richtext/types";
import { StatusItemDto } from "../../../services/status-items.service";
import { useTypographyStyles } from "../../../styles/typography.styles";

/**
 * Max length of a discussion description, in markup characters.
 *
 * Shared with CreateDiscussionDialog / EditDiscussionDialog, which validate
 * against it before submit — keep it here so the input's hard cap and the
 * dialogs' validation can't drift apart. Purely a frontend limit: the DB
 * column is TEXT and the API does no length validation, so raising this
 * needs no migration.
 *
 * Separate from the comment limit in CommentComposer — descriptions and
 * comments are deliberately allowed to differ.
 */
export const MAX_DESCRIPTION_LENGTH = 1000;

const useStyles = makeStyles({
  form: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  charCount: {
    textAlign: "right",
    marginTop: tokens.spacingVerticalXS,
  },
});

interface DiscussionFormProps {
  title: string;
  description: RichTextValue;
  onTitleChange: (value: string) => void;
  onDescriptionChange: (value: RichTextValue) => void;
  users?: MentionUser[];
  statusItems?: StatusItemDto[];
  errors?: {
    title?: string;
    description?: string;
  };
  /** Auto-focus the title input on mount. Used by the create wizard so the
   *  user can type immediately after picking a type; left off for edit so
   *  focus doesn't jump away from whatever the user clicked. */
  autoFocusTitle?: boolean;
  children?: React.ReactNode;
}

const DiscussionForm: React.FC<DiscussionFormProps> = ({
  title,
  description,
  onTitleChange,
  onDescriptionChange,
  users,
  statusItems,
  errors,
  autoFocusTitle,
  children,
}) => {
  const styles = useStyles();
  const typography = useTypographyStyles();

  const titleLength = title.length;

  return (
    <div className={styles.form}>
      <Field
        label="Title"
        required
        validationState={errors?.title ? "error" : undefined}
        validationMessage={errors?.title}
      >
        <Input
          value={title}
          onChange={(_e, data) => onTitleChange(data.value)}
          placeholder="Enter discussion title..."
          maxLength={100}
          input={autoFocusTitle ? { autoFocus: true } : undefined}
        />
        <Text className={`${typography.caption} ${styles.charCount}`}>
          {titleLength}/100
        </Text>
      </Field>

      {children}

      <Field
        label="Description"
        required
        validationState={errors?.description ? "error" : undefined}
        validationMessage={errors?.description}
      >
        <RichTextInput
          value={description}
          onChange={onDescriptionChange}
          placeholder="Describe your discussion..."
          maxLength={MAX_DESCRIPTION_LENGTH}
          minHeight="120px"
          users={users}
          statusItems={statusItems}
          hintText={<>Type <strong>@</strong> to mention users, <strong>#</strong> to tag sheets/items, <strong>[</strong> for cell references</>}
        />
      </Field>
    </div>
  );
};

export default DiscussionForm;
